import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTopUsers } from 'services/admin/adminService';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { AlertTriangle, Inbox } from 'lucide-react';
import { getErrorMessage } from 'utils/errors';

// Interface matching actual API response
interface TopUserApiResponse {
  _id: string;
  userId: string;
  name: string;
  email: string;
  totalRevenue: number;
  averagePayment: number;
  subscriptionType: string;
  joinDate: string;
  lifetimeValue: number;
  paymentCount?: number;
  lastPaymentDate?: string;
  socialAccounts?: number;
  totalActions?: number;
}

interface TopUsersCardProps {
  metric?: 'revenue' | 'social_accounts' | 'actions' | 'engagement';
  limit?: number;
}

export function TopUsersCard({ metric = 'revenue', limit = 5 }: TopUsersCardProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [users, setUsers] = useState<TopUserApiResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardHoverBg = isDark ? '#162030' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const errorText = isDark ? '#F87171' : '#DC2626';

  useEffect(() => {
    const fetchTopUsers = async () => {
      try {
        setLoading(true);
        const data = await getTopUsers(metric, limit);
        // Cast to match actual API response structure
        setUsers(data.users as unknown as TopUserApiResponse[]);
        setError(null);
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Failed to fetch top users'));
      } finally {
        setLoading(false);
      }
    };

    fetchTopUsers();
  }, [metric, limit]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Spinner size="medium" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-sm" style={{ color: errorText }}>
        <AlertTriangle size={20} style={{ marginBottom: 8 }} />
        <p>{error}</p>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8" style={{ color: subtitleColor }}>
        <Inbox size={28} style={{ marginBottom: 8 }} />
        <p className="text-sm">{t('admin.topUsers.noUsers', 'No users found')}</p>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Calculate subscription length in months from joinDate
  const getSubscriptionMonths = (joinDate: string): number => {
    if (!joinDate) return 0;
    const join = new Date(joinDate);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - join.getTime());
    const diffMonths = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 30));
    return diffMonths;
  };

  return (
    <div className="space-y-3">
      {users.map((user, index) => (
        <div
          key={user._id || user.userId || `user-${index}`}
          className="flex items-center justify-between p-3 rounded-lg transition-colors"
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          style={{
            background: hoveredIndex === index ? cardHoverBg : cardBg,
            transition: 'background-color 0.15s'
          }}
        >
          <div className="flex items-center gap-3 flex-1">
            <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full text-xs font-bold">
              #{index + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: titleColor }}>{user.name || 'N/A'}</div>
              <div className="text-xs truncate" style={{ color: subtitleColor }}>{user.email || 'N/A'}</div>
            </div>
          </div>
          <div className="text-right ml-4">
            <div className="text-sm font-semibold" style={{ color: titleColor }}>
              {metric === 'revenue' && formatCurrency(user.totalRevenue || user.lifetimeValue || 0)}
              {metric === 'social_accounts' && `${user.socialAccounts || 0} accounts`}
              {metric === 'actions' && `${(user.totalActions || 0).toLocaleString()} actions`}
            </div>
            <div className="text-xs" style={{ color: subtitleColor }}>
              {user.subscriptionType || 'N/A'} • {getSubscriptionMonths(user.joinDate)}mo
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
